import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Send, Check, Loader2 } from 'lucide-react';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';
import { useTeamChatStore } from '../../state/teamChatStore';
import { useAuthStore } from '../../state/authStore';
import { getBackendBaseUrl } from '../../lib/backendUrl';

const BACKEND = getBackendBaseUrl();

/**
 * Share-to-chat button. Drop it next to any piece of content.
 *
 * @param {object} props
 * @param {'shared_ai'|'shared_news'|'shared_research'|'shared_signal'} props.messageType
 * @param {object} props.payload - { title, summary, source?, url?, symbol?, signal_type?, type }
 * @param {string} [props.defaultComment] - Pre-filled comment text
 * @param {string} [props.className]
 */
export default function ShareToChat({ messageType, payload, defaultComment = '', className = '' }) {
  const user = useAuthStore(s => s.user);
  const sendMessage = useTeamChatStore(s => s.sendMessage);

  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [comment, setComment] = useState(defaultComment);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    axios.get(`${BACKEND}/api/orgs`).then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setOrgs(list);
      if (list.length && !selectedOrgId) setSelectedOrgId(list[0].id);
    }).catch(() => {});
  }, [open, user?.id]);

  const handleShare = async () => {
    if (!selectedOrgId) return;
    setSending(true);
    const text = comment.trim() || payload?.title || 'Shared content';
    const result = await sendMessage(selectedOrgId, text, messageType, payload);
    setSending(false);
    if (result) {
      setSent(true);
      toast.success('Shared to team chat');
      setTimeout(() => { setOpen(false); setSent(false); setComment(''); }, 1200);
    } else {
      toast.error('Failed to share');
    }
  };

  if (!user?.id) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-1.5 text-gray-400 hover:text-emerald-400 ${className}`}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span className="text-xs">Share</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 bg-gray-900 border-gray-700 p-3" align="end">
        <p className="text-xs font-semibold text-gray-300 mb-2">Share to Team Chat</p>

        {payload?.title && (
          <div className="bg-gray-800 rounded-md p-2 mb-2">
            <p className="text-xs text-gray-300 font-medium line-clamp-2">{payload.title}</p>
            {payload.summary && <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">{payload.summary}</p>}
          </div>
        )}

        {orgs.length > 1 && (
          <select
            value={selectedOrgId}
            onChange={e => setSelectedOrgId(e.target.value)}
            className="w-full bg-gray-800 text-gray-300 text-xs rounded border border-gray-700 px-2 py-1.5 mb-2"
          >
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}

        <Input
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Add a comment..."
          className="bg-gray-800 border-gray-700 text-gray-100 text-xs mb-2"
        />

        <Button
          onClick={handleShare}
          disabled={sending || sent || !selectedOrgId}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-sm gap-2"
          size="sm"
        >
          {sent ? (
            <><Check className="w-3.5 h-3.5" /> Shared!</>
          ) : sending ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending...</>
          ) : (
            <><Send className="w-3.5 h-3.5" /> Share</>
          )}
        </Button>

        {!orgs.length && (
          <p className="text-[10px] text-gray-500 mt-1 text-center">
            Create an organization first to use team chat.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
